import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  HandCoins, Users, TrendingUp, Euro, Gift, Copy, ExternalLink,
  CheckCircle, Clock, Sparkles, Plus, Search, Filter,
  Share2, Mail, Building2, Phone, Calendar, ArrowRight,
  Target, Award, Briefcase, Loader2, CreditCard
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '../../lib/axios';

const AffiliateDashboard = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recentLeads, setRecentLeads] = useState([]);
  const [profileComplete, setProfileComplete] = useState(true);
  const [stats, setStats] = useState({
    total_referrals: 0,
    converted_referrals: 0,
    pending_commission: 0,
    total_earned: 0,
    conversion_rate: 0,
    last_earning: null
  });

  // Fetch affiliate data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [leadsRes, commissionsRes, profileRes] = await Promise.all([
          api.get('/affiliates/me/leads'),
          api.get('/affiliates/me/commissions'),
          api.get('/affiliates/me/profile')
        ]);
        
        const leads = leadsRes.data?.leads || [];
        setRecentLeads(leads.slice(0, 5));
        
        const commissions = commissionsRes.data || {};
        const totalLeads = leads.length;
        const converted = leads.filter(l => l.status === 'converted' || l.is_client).length;
        
        // Vérifier si le profil est complété (IBAN + BIC requis)
        const profile = profileRes.data || {};
        setProfileComplete(profile.onboarding_completed === true || (profile.iban && profile.bic));
        
        setStats({
          total_referrals: totalLeads,
          converted_referrals: converted,
          pending_commission: commissions.pending_commission || 0,
          total_earned: commissions.total_earned || 0,
          conversion_rate: totalLeads > 0 ? Math.round((converted / totalLeads) * 100) : 0,
          last_earning: null
        });
      } catch (error) {
        console.error('Error fetching affiliate data:', error);
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Calculate conversion rate
  const conversionRate = stats.total_referrals > 0 
    ? Math.round((stats.converted_referrals / stats.total_referrals) * 100) 
    : 0;

  // Referral link
  const referralLink = `${window.location.origin}/site?ref=${user?.id || 'demo'}`;

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Lien de parrainage copié !');
  };

  const getStatusBadge = (status) => {
    const config = {
      'new': { color: 'bg-blue-100 text-blue-700', label: 'Nouveau' },
      'contacted': { color: 'bg-amber-100 text-amber-700', label: 'Contacté' },
      'qualified': { color: 'bg-purple-100 text-purple-700', label: 'Qualifié' },
      'converted': { color: 'bg-emerald-100 text-emerald-700', label: 'Converti' },
      'lost': { color: 'bg-slate-100 text-slate-700', label: 'Perdu' },
    };
    return config[status] || config['new'];
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Alerte profil incomplet */}
        {!profileComplete && !loading && (
          <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-amber-100">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-800">Complétez votre profil</p>
                  <p className="text-sm text-amber-700">
                    Renseignez vos coordonnées bancaires (IBAN + BIC) pour recevoir vos commissions.
                  </p>
                </div>
              </div>
              <Link to="/affiliate/onboarding">
                <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                  Compléter mon profil
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Welcome Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Bonjour {user?.first_name} ! 👋
              </h1>
              <p className="text-slate-500 mt-1">
                Espace Apporteur d'Affaires • Gérez vos leads et commissions
              </p>
            </div>
            <Button 
              onClick={copyReferralLink}
              className="hidden md:flex gap-2"
              style={{ backgroundColor: theme.colors?.accent }}
            >
              <Share2 className="w-4 h-4" />
              Copier mon lien
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <Euro className="w-5 h-5 text-emerald-600" />
                </div>
                <Badge className="bg-emerald-100 text-emerald-700">Total</Badge>
              </div>
              <div className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                {stats.total_earned.toLocaleString('fr-FR')}€
              </div>
              <p className="text-sm text-slate-500">Gains totaux</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <Badge className="bg-amber-100 text-amber-700">En attente</Badge>
              </div>
              <div className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                {stats.pending_commission.toLocaleString('fr-FR')}€
              </div>
              <p className="text-sm text-slate-500">Commissions à venir</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <div className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                {stats.total_referrals}
              </div>
              <p className="text-sm text-slate-500">Leads générés</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <div className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                {conversionRate}%
              </div>
              <p className="text-sm text-slate-500">Taux de conversion</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Leads */}
          <Card className="lg:col-span-2 border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-lg">Mes derniers leads</CardTitle>
                <CardDescription>Suivez vos opportunités en cours</CardDescription>
              </div>
              <Link to="/affiliate/leads">
                <Button variant="outline" size="sm">
                  Voir tout
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : recentLeads.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">Aucun lead pour le moment</p>
                  <Link to="/affiliate/leads">
                    <Button variant="outline" size="sm" className="mt-3">
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter un lead
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentLeads.map((lead) => {
                    const statusConfig = getStatusBadge(lead.status);
                    return (
                      <div 
                        key={lead.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium" style={{ color: theme.colors?.primary }}>
                              {lead.client_name || lead.company}
                            </p>
                            <p className="text-sm text-slate-500">{lead.service || lead.contact}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-medium text-emerald-600">{(lead.estimated_value || 0).toLocaleString('fr-FR')}€</p>
                            <p className="text-xs text-slate-400">{lead.created_at?.split('T')[0] || lead.date}</p>
                          </div>
                          <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions & Referral */}
          <div className="space-y-6">
            {/* Referral Link Card */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-500 to-green-600 text-white">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Share2 className="w-5 h-5" />
                  <span className="font-semibold">Lien de parrainage</span>
                </div>
                <div className="bg-white/20 backdrop-blur rounded-lg p-3 mb-3">
                  <p className="text-sm truncate font-mono">{referralLink}</p>
                </div>
                <Button 
                  onClick={copyReferralLink}
                  className="w-full bg-white text-emerald-600 hover:bg-emerald-50"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copier le lien
                </Button>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Actions rapides</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/affiliate/leads" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Plus className="w-4 h-4" />
                    Ajouter un lead
                  </Button>
                </Link>
                <Link to="/affiliate/commissions" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <HandCoins className="w-4 h-4" />
                    Voir mes commissions
                  </Button>
                </Link>
                <Link to="/affiliate/contract" className="block">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Award className="w-4 h-4" />
                    Mon contrat
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Commission Info */}
            <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Gift className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm" style={{ color: theme.colors?.primary }}>
                      Commission : 10% du premier projet
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Payée dès validation du paiement client
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AffiliateDashboard;
